import { FocusEvent, useEffect, useMemo, useRef, useState } from "react";

import { Dataset } from "@/types/dataset";
import { debounce } from "@/utils/debounce";

import SliderInput from "./SliderInput";

interface Props {
  data: Dataset;
  max: number;
  onChangeSlider?: (value: number[]) => void;
  onChangeInput?: (value: number) => void;
}

const COMMIT_DEBOUNCE_MS = 400;
const MIN_RANKING = 1;

export const RankSlider = ({
  data,
  max,
  onChangeSlider,
  onChangeInput
}: Props) => {
  /** Committed ranking steps from the store */
  const storedRankingStepValue = data.style.rankConfig?.maxRanking ?? 1;

  /** Local value for display only, commited value will be persisted to Redux store via onChangeInput or onChangeSlider */
  const [localRankingStepValue, setLocalRankingStepValue] = useState<
    number | null
  >(null);

  const displayValue = localRankingStepValue ?? storedRankingStepValue;

  /** The dataset list rebuilds its rank handlers inside render, so both props
   * arrive with a new identity on every parent re-render. Reaching them through
   * refs keeps that churn out of the debounced instance below. */
  const onChangeSliderRef = useRef(onChangeSlider);
  const onChangeInputRef = useRef(onChangeInput);

  useEffect(() => {
    onChangeSliderRef.current = onChangeSlider;
    onChangeInputRef.current = onChangeInput;
  }, [onChangeSlider, onChangeInput]);

  const debouncedCommit = useMemo(
    () =>
      debounce((value: number, source: "slider" | "input") => {
        setLocalRankingStepValue(null);
        if (source === "slider") onChangeSliderRef.current?.([value]);
        else onChangeInputRef.current?.(value);
      }, COMMIT_DEBOUNCE_MS),
    []
  );

  /** Commit as soon as the control loses focus, rather than waiting out the
   * remaining debounce. Panels that read their settings in a click handler
   * (the catchment popup's Save) snapshot the store before an unmount cleanup
   * could ever run, so the pending edit has to land while the control is still
   * mounted — blur is the last moment that is true. */
  const flushOnLeave = (event: FocusEvent<HTMLDivElement>) => {
    // Moving between the slider and the number input is still the same edit.
    if (event.currentTarget.contains(event.relatedTarget)) return;
    debouncedCommit.flush();
  };

  /** Backstop for a close that never blurs (Esc, a route change, the dataset
   * being removed). Commits rather than cancels — the value the user dialled
   * in is not ours to discard. Reducers no-op when the target is already gone. */
  useEffect(() => () => debouncedCommit.flush(), [debouncedCommit]);

  return (
    <div className="flex flex-col gap-2 w-full" onBlur={flushOnLeave}>
      <p className="text-xs text-txtPlaceholder">Max Ranking</p>
      <SliderInput
        disabled={!data.style.rankConfig?.isEnabled || max <= 1}
        valueSlider={[displayValue]}
        onChangeSlider={(value) => {
          const nextValue = Math.max(MIN_RANKING, value[0]);
          setLocalRankingStepValue(nextValue);
          debouncedCommit(nextValue, "slider");
        }}
        valueInput={displayValue}
        onChangeInput={(value) => {
          setLocalRankingStepValue(value);
          debouncedCommit(value, "input");
        }}
        min={MIN_RANKING}
        max={max}
      />
    </div>
  );
};
